import {
  Heart,
  ImageIcon,
  MessageCircle,
  MoreHorizontal,
  Send,
  Share2,
  ThumbsUp,
  X,
} from "lucide-react";
import { twMerge } from "tailwind-merge";

import { Avatar } from "@/components/avatar";

import type { ScheduledPost } from "../scheduledPost";

import { DesignImage } from "../../media/DesignImage";
import { PostCaption } from "./PostCaption";
import { PostVideo } from "./PostVideo";

export function VerticalPostPreview({
  format,
  platform,
  post,
}: {
  format: "reel" | "story";
  platform: "facebook" | "instagram";
  post: ScheduledPost;
}) {
  const platformLabel = platform === "instagram" ? "Instagram" : "Facebook";
  const formatLabel = format === "reel" ? "Reel" : "Story";

  return (
    <article
      aria-label={`${platformLabel} ${formatLabel} preview`}
      className="relative mx-auto aspect-[9/16] w-full max-w-[280px] overflow-hidden rounded-2xl bg-bg-secondary text-white shadow-md"
    >
      {post.video ? (
        <PostVideo
          className="absolute inset-0 size-full object-cover"
          title={post.title}
          video={post.video}
        />
      ) : post.images[0] ? (
        <DesignImage
          alt={`Post image for ${post.title}`}
          className="absolute inset-0 size-full object-cover"
          sources={post.images[0]}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <ImageIcon className="size-10 text-fg-quaternary" />
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent via-25% to-black/60" />

      {format === "story" ? (
        <StoryOverlay platform={platform} post={post} />
      ) : (
        <ReelOverlay platform={platform} post={post} />
      )}
    </article>
  );
}

function StoryOverlay({
  platform,
  post,
}: {
  platform: "facebook" | "instagram";
  post: ScheduledPost;
}) {
  return (
    <>
      <div className="absolute inset-x-0 top-0 flex flex-col gap-md p-md">
        <div className="flex gap-xs">
          {(platform === "instagram" ? [0, 1, 2] : [0]).map((segment) => (
            <span
              className={twMerge(
                "h-0.5 flex-1 rounded-full bg-white/40",
                segment === 0 && "bg-white",
              )}
              key={segment}
            />
          ))}
        </div>
        <div className="flex items-center gap-sm">
          <AccountAvatar name={post.account.name} platform={platform} />
          <span className="truncate text-sm font-semibold">
            {post.account.name}
          </span>
          <span className="text-xs text-white/70">Now</span>
          <X className="ml-auto size-5" />
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 flex items-center gap-md p-md">
        <span className="flex-1 truncate rounded-full border border-white/70 px-lg py-sm text-sm text-white/80">
          {platform === "instagram" ? "Send message" : "Reply…"}
        </span>
        {platform === "instagram" ? (
          <>
            <Heart className="size-6 shrink-0" />
            <Send className="size-6 shrink-0" />
          </>
        ) : (
          <>
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-utility-blue-500">
              <ThumbsUp className="size-4" />
            </span>
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-utility-error-500">
              <Heart className="size-4" />
            </span>
          </>
        )}
      </div>
    </>
  );
}

function ReelOverlay({
  platform,
  post,
}: {
  platform: "facebook" | "instagram";
  post: ScheduledPost;
}) {
  const actions =
    platform === "instagram"
      ? [Heart, MessageCircle, Send, MoreHorizontal]
      : [ThumbsUp, MessageCircle, Share2, MoreHorizontal];

  return (
    <>
      <span className="absolute left-md top-md text-md font-semibold">
        Reels
      </span>

      <div className="absolute bottom-lg right-md flex flex-col items-center gap-lg">
        {actions.map((Icon, index) => (
          <Icon className="size-6" key={index} />
        ))}
      </div>

      <div className="absolute bottom-0 left-0 right-12 flex flex-col gap-sm p-md">
        <div className="flex items-center gap-sm">
          <AccountAvatar name={post.account.name} platform={platform} />
          <span className="truncate text-sm font-semibold">
            {post.account.name}
          </span>
          <span
            className={twMerge(
              "shrink-0 rounded-md px-sm py-0.5 text-xs font-semibold",
              platform === "instagram"
                ? "border border-white/80"
                : "bg-utility-blue-500",
            )}
          >
            Follow
          </span>
        </div>
        {post.caption && (
          <p className="line-clamp-2 text-sm">
            <PostCaption caption={post.caption} />
          </p>
        )}
      </div>
    </>
  );
}

function AccountAvatar({
  name,
  platform,
}: {
  name: string;
  platform: "facebook" | "instagram";
}) {
  return (
    <Avatar
      alt={name}
      className={twMerge(
        "size-8 rounded-full ring-2",
        platform === "instagram"
          ? "ring-utility-pink-500"
          : "ring-utility-blue-500",
      )}
      initials={name.slice(0, 2).toUpperCase()}
    />
  );
}
